import { StyleSheet } from "react-native";

// One token set for the dark store screens (Receiving & Racks, racking hub,
// DS routing) and the pieces they share with PartnerRoutingAndPacking
// (bottom sheet, sheet buttons, crate scanner).
//
// The two features started as "copy, don't share" forks and disagree on two
// values, so each keeps its own name here and nothing on screen changes:
//   - textPrimary (#000) is the dark store text; textHeading (#1E293B) is
//     the slate PartnerRoutingAndPacking uses as its textPrimary.
//   - primary (#00AB59, @constants/color plain_green) is the dark store
//     green; accent is PartnerRoutingAndPacking's { bg, bgDark, fg } primary.
// PartnerRoutingAndPacking/styles.js maps its old names onto these.

export const tokens = {
  bg: "#F1F5F9",
  surface: "#FFFFFF",
  surfaceHover: "#F8FAFC",
  borderLight: "#E2E8F0",
  borderMedium: "#CBD5E1",
  textPrimary: "#000000",
  textHeading: "#1E293B",
  textSecondary: "#64748B",
  textTertiary: "#94A3B8",

  primary: "#00AB59", // matches @constants/color plain_green
  primaryDark: "#00893F",
  accent: { bg: "#22C55E", bgDark: "#16A34A", fg: "#FFFFFF" },
  disabled: "#C4C4C4",
  warningBg: "#FFF4E5",
  warningBorder: "#FDBA74",
  warningText: "#9A5B00",
  successBg: "#E6F7EE",
  successBorder: "#86E0B0",
  successText: "#00893F",
  errorText: "#C41A16",
  warning: "#FB923C",
  error: "#EF4444",
  info: "#3B82F6",

  // Capacity bar ramp (PartnerRoutingAndPacking ProgressBar).
  capacity: {
    empty: "#E2E8F0",
    low: "#22C55E", // < 70%
    medium: "#FB923C", // 71-90
    high: "#EF4444", // 91-99
    full: "#3B82F6", // 100
  },

  pill: {
    // Receiving chips (spec p2 frame 2): amber "TO BE RACKED", grey
    // "N OF M RACKS", green "RACK B-04".
    toRack: { bg: "#FEF3C7", fg: "#92400E", border: "#FDE68A" },
    inProgress: { bg: "#F1F5F9", fg: "#475569", border: "#E2E8F0" },
    racked: { bg: "#DCFCE7", fg: "#15803D", border: "#BBF7D0" },
    disabled: { bg: "#F1F5F9", fg: "#94A3B8", border: "#E2E8F0" },
    // PartnerRoutingAndPacking StatusBadge states.
    preferred: { bg: "#EEF2FF", fg: "#4F46E5", border: "#C7D2FE" },
    new: { bg: "#DBEAFE", fg: "#1E40AF", border: "#BFDBFE" },
    rescheduled: { bg: "#FEF3C7", fg: "#A16207", border: "#FDE68A" },
    packed: { bg: "#DCFCE7", fg: "#16A34A", border: "#BBF7D0" },
    unpacked: { bg: "#FEE2E2", fg: "#DC2626", border: "#FECACA" },
    partial: { bg: "#FED7AA", fg: "#EA580C", border: "#FDBA74" },
    misroute: { bg: "#FFEDD5", fg: "#C2410C", border: "#FED7AA" },
    neutral: { bg: "#F1F5F9", fg: "#475569", border: "#E2E8F0" },
  },

  // Per-issue palette (PartnerRoutingAndPacking marking flow).
  reason: {
    red: { bg: "#FEE2E2", fg: "#DC2626", border: "#FECACA" },
    orange: { bg: "#FFEDD5", fg: "#C2410C", border: "#FED7AA" },
    gold: { bg: "#FEF3C7", fg: "#A16207", border: "#FDE68A" },
    purple: { bg: "#F3E8FF", fg: "#7E22CE", border: "#E9D5FF" },
    blue: { bg: "#DBEAFE", fg: "#1E40AF", border: "#BFDBFE" },
    default: { bg: "#F1F5F9", fg: "#475569", border: "#E2E8F0" },
  },

  spacing: {
    tiny: 4,
    small: 8,
    medium: 12,
    large: 16,
    xlarge: 24,
    page: 16,
    section: 20,
    card: 16,
  },
  radius: { sm: 4, md: 8, lg: 12, xl: 16, full: 9999 },

  modalScrim: "rgba(15,23,42,0.55)",
  transparent: "transparent",
};

export const cardElevation = {
  shadowColor: "#0F172A",
  shadowOffset: { width: 0, height: 1 },
  shadowOpacity: 0.06,
  shadowRadius: 3,
  elevation: 1,
};

export const sharedStyles = StyleSheet.create({
  flex1: { flex: 1 },
  screen: { flex: 1, backgroundColor: tokens.bg },
  centered: { flex: 1, alignItems: "center", justifyContent: "center" },
  card: {
    backgroundColor: tokens.surface,
    borderRadius: tokens.radius.lg,
    borderWidth: 1,
    borderColor: tokens.borderLight,
    padding: tokens.spacing.large,
    marginHorizontal: tokens.spacing.large,
    marginBottom: tokens.spacing.medium,
    ...cardElevation,
  },
  rowSpread: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  pillBase: {
    paddingHorizontal: tokens.spacing.medium,
    paddingVertical: 6,
    borderRadius: tokens.radius.full,
    borderWidth: 1,
    alignSelf: "flex-start",
  },
});
